import Phaser from "phaser";
import { GAME_HEIGHT, STAGE_CONFIGS, DEBUG } from "../config";
import { RunState, createRunState } from "../state/RunState";
import { EnemySentence } from "../entities/EnemySentence";
import { FallingChar } from "../entities/FallingChar";
import { InputSystem } from "../systems/InputSystem";
import { WordBank } from "../systems/WordBank";
import { Spawner } from "../systems/Spawner";
import { DifficultySystem } from "../systems/DifficultySystem";
import { ScoringSystem } from "../systems/ScoringSystem";
import { PurchaseGate } from "../systems/PurchaseGate";
import { HUD } from "../ui/HUD";
import { NextCharPanel } from "../ui/NextCharPanel";

/** メインゲームシーン */
export class PlayScene extends Phaser.Scene {
  private state!: RunState;
  private enemy!: EnemySentence;
  private fallingChars: FallingChar[] = [];
  private inputSystem!: InputSystem;
  private wordBank!: WordBank;
  private spawner!: Spawner;
  private difficulty!: DifficultySystem;
  private scoring!: ScoringSystem;
  private gate!: PurchaseGate;
  private hud!: HUD;
  private nextCharPanel!: NextCharPanel;

  constructor() {
    super({ key: "PlayScene" });
  }

  create(): void {
    // 背景
    this.cameras.main.setBackgroundColor("#111122");

    // システム初期化
    this.wordBank = new WordBank();
    this.difficulty = new DifficultySystem();
    this.scoring = new ScoringSystem();
    this.gate = new PurchaseGate();
    this.spawner = new Spawner(this.wordBank, this.difficulty, this.gate);

    // 初期文章生成
    const gatedStage = this.gate.applyGate("S1");
    const config = this.difficulty.getConfig(gatedStage);
    const initialText = this.wordBank.pick(config);

    // state初期化
    this.state = createRunState(initialText);

    // Enemy生成
    this.enemy = new EnemySentence(this, initialText, config.fallSpeed);
    this.fallingChars = [];

    // Input
    this.inputSystem = new InputSystem(this);
    this.inputSystem.bind(this.enemy, (result) => {
      if (result.pickedIndex === null) return;
      this.handleInput(result.pickedIndex);
    });

    // pointerdownイベント登録
    this.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      this.inputSystem.handlePointerDown(pointer);
    });

    // UI
    this.hud = new HUD(this);
    this.nextCharPanel = new NextCharPanel(this);
    this.nextCharPanel.update(this.state.nextChar);
  }

  update(_time: number, delta: number): void {
    const dt = delta / 1000;

    // Enemy更新
    this.enemy.update(dt);

    // FallingChars更新
    this.fallingChars = this.fallingChars.filter((fc) => {
      fc.update(dt);
      return fc.isAlive();
    });

    // GameOver判定
    if (this.enemy.isReachedBottom(GAME_HEIGHT)) {
      this.scene.start("GameOverScene", {
        score: this.state.score,
        maxComboMult: this.state.maxComboMult,
      });
      return;
    }

    // ステージ昇格チェック
    const newStage = this.difficulty.getStageByScore(this.state.score);
    const noDowngrade = this.difficulty.ensureNoDowngrade(this.state.stage, newStage);
    const gatedStage = this.gate.applyGate(noDowngrade);

    if (gatedStage !== this.state.stage) {
      this.state.stage = gatedStage;
      const stageConfig = STAGE_CONFIGS[gatedStage];
      this.enemy.setFallSpeed(stageConfig.fallSpeed);
      this.hud.showStageUp(gatedStage);

      if (DEBUG) {
        console.log(`[Play] stage up: ${gatedStage}`);
      }
    }

    // HUD更新
    const progress = this.difficulty.getProgress(this.state.stage, this.state.score);
    this.hud.update(
      this.state.score,
      this.state.comboMult,
      this.state.stage,
      progress.ratio,
      progress.nextScore
    );
  }

  private handleInput(pickedIndex: number): void {
    if (pickedIndex === this.state.nextIndex) {
      // Hit
      const hitResult = this.enemy.hit(pickedIndex);

      // FallingChar生成
      const fc = new FallingChar(this, hitResult.x, hitResult.y, hitResult.char);
      this.fallingChars.push(fc);

      // スコア計算
      const nowMs = performance.now();
      this.scoring.onHit(this.state, nowMs);

      // nextIndex進行
      this.state.nextIndex++;

      if (this.state.nextIndex >= this.state.sentence.length) {
        // 文完走
        this.scoring.onSentenceCompleted(this.state);
        this.spawnNextSentence();
      } else {
        this.state.nextChar = this.state.sentence.charAt(this.state.nextIndex);
      }

      this.nextCharPanel.update(this.state.nextChar);
    } else {
      // Miss
      this.scoring.onMiss(this.state);
    }
  }

  private spawnNextSentence(): void {
    // 旧enemy破棄
    this.enemy.destroy();

    // 新文章生成
    const text = this.spawner.next(this.state);
    const stageConfig = STAGE_CONFIGS[this.state.stage];
    this.enemy = new EnemySentence(this, text, stageConfig.fallSpeed);

    // Input更新
    this.inputSystem.setEnemy(this.enemy);

    // NextCharPanel更新
    this.nextCharPanel.update(this.state.nextChar);
  }
}
